import {
  logEvent,
  setUserProperties,
  type Analytics,
} from 'firebase/analytics';
import {
  initFirebaseAnalytics,
  setAnalyticsCollection,
} from '@/shared/lib/firebase';

/**
 * Funnel analytics — a thin, typed layer over Firebase / Google Analytics.
 *
 * WHY THIS EXISTS: the most common reasons a user uploads an angle but never
 * renders happen entirely client-side (subscription/token/queue paywalls,
 * empty-refine disabled button) and abort BEFORE any request reaches the API,
 * so the server is blind to them. This records the user's intent (the Generate
 * click) and the reason each render attempt is blocked, so the drop-off is
 * observable.
 *
 * CROSS-PLATFORM CONTRACT: the event/param/user-property names below are the
 * shared schema — they MUST stay identical to Apple
 * (`apps/apple/.../AnalyticsManager.swift`) and Android
 * (`apps/android/.../AnalyticsTracker.kt`). Renaming an event after data starts
 * flowing breaks funnel comparability across platforms, so treat these strings
 * as load-bearing.
 *
 * No-ops outside production (and in unsupported/SSR contexts) because
 * `initFirebaseAnalytics()` resolves to `null` there — see firebase.ts.
 */

export const AnalyticsEvent = {
  /**
   * Early-funnel visibility (registration → onboarding → first upload). These
   * fire BEFORE `angle_upload_completed` so we can tell "wrong product / left on
   * onboarding" from "hidden bug blocking first upload". Event/param strings are
   * a byte-identical cross-platform contract with Apple/Android — do not rename.
   */
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_SLIDE_VIEWED: 'onboarding_slide_viewed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_DISMISSED: 'onboarding_dismissed',
  /** Upload INTENT — the counterpart to `angle_upload_completed` for measuring
   *  intent→completion drop-off. */
  ANGLE_UPLOAD_STARTED: 'angle_upload_started',
  /** A real upload/processing failure (NOT a plain OS-picker cancel). */
  ANGLE_UPLOAD_FAILED: 'angle_upload_failed',
  /** The empty buildings/projects home (no projects yet) is shown. */
  HOME_EMPTY_STATE_VIEWED: 'home_empty_state_viewed',
  ANGLE_UPLOAD_COMPLETED: 'angle_upload_completed',
  RENDER_SHEET_OPENED: 'render_sheet_opened',
  /** Fired FIRST in the Generate handler, before any gate — captures intent. */
  GENERATE_TAPPED: 'generate_tapped',
  /** Fired in each preflight gate that aborts before the POST. */
  GENERATE_BLOCKED: 'generate_blocked',
  RENDER_CREATED: 'render_created',
  RENDER_ERROR: 'render_error',
  /**
   * Facade Pack conversion funnel (Facade Pack pivot). WEB is read-only for
   * purchases — it logs the SHOWN events for every pack surface (pack /
   * builder), plus `pack_purchase_started` when the user taps a "get in the
   * app" store badge (the WEB analog of starting a purchase — the actual
   * checkout + completion happen on mobile, so WEB never logs `*_completed`).
   * `surface` / `trigger` / `project_bound` params and the event names are an
   * identical cross-platform contract.
   */
  PACK_PAYWALL_SHOWN: 'pack_paywall_shown',
  PACK_PURCHASE_STARTED: 'pack_purchase_started',
  BUILDER_SUBSCRIBE_STARTED: 'builder_subscribe_started',
} as const;
export type AnalyticsEvent =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

/** Which Facade Pack surface a `pack_paywall_shown` event came from. Shared
 *  vocabulary — keep identical to Apple/Android. */
export const PaywallSurface = {
  PACK: 'pack',
  BUILDER: 'builder',
} as const;
export type PaywallSurface =
  (typeof PaywallSurface)[keyof typeof PaywallSurface];

/** What surfaced a Facade Pack paywall. Coarse trigger derived from the server
 *  payload's primary-action kind (the limit code is not carried in the
 *  payload), or "storefront" when opened from the plans page. */
export const PackTrigger = {
  PURCHASE_PACK: 'purchase_pack',
  BUILDER: 'builder',
  STOREFRONT: 'storefront',
} as const;
export type PackTrigger = (typeof PackTrigger)[keyof typeof PackTrigger];

/** Shared vocabulary for why a render attempt did not reach the API. */
export const BlockReason = {
  SUBSCRIPTION_REQUIRED: 'subscription_required',
  INSUFFICIENT_TOKENS: 'insufficient_tokens',
  // Free-tier designs allowance spent (Facade Pack pivot) — value mirrors the
  // server queue-status block_reason; cross-platform analytics contract.
  FREE_DESIGNS_EXHAUSTED: 'free_designs_exhausted',
  QUEUE_FULL: 'queue_full',
  EMPTY_REFINE: 'empty_refine',
  GOALS_FAILED: 'goals_failed',
  SETTINGS_LOADING: 'settings_loading',
} as const;
export type BlockReason = (typeof BlockReason)[keyof typeof BlockReason];

/** Shared vocabulary for render_error after the POST left the device. */
export const ErrorType = {
  NETWORK: 'network',
  OFFLINE: 'offline',
  SERVER: 'server',
  VALIDATION: 'validation',
  UNKNOWN: 'unknown',
} as const;
export type ErrorType = (typeof ErrorType)[keyof typeof ErrorType];

type EventParams = Record<string, string | number | boolean>;

function withAnalytics(fn: (analytics: Analytics) => void): void {
  void initFirebaseAnalytics().then((analytics) => {
    if (analytics) fn(analytics);
  });
}

export function trackEvent(
  event: AnalyticsEvent,
  params: EventParams = {}
): void {
  withAnalytics((analytics) => logEvent(analytics, event, params));
}

export interface AnalyticsIdentity {
  planSlug?: string | null;
  planType?: string | null;
  /** Passes are mobile-only — always false on web (no purchase flow here). */
  hasProPass?: boolean;
  hasDesignerPass?: boolean;
  appLocale: string;
}

/**
 * Set the slowly-changing funnel dimensions. Call wherever the profile / plan
 * (re)loads. `platform` is always `web`; passes are always false here.
 */
export function setAnalyticsUser(identity: AnalyticsIdentity): void {
  withAnalytics((analytics) => {
    setUserProperties(analytics, {
      plan_slug: identity.planSlug ?? '',
      plan_type: identity.planType ?? '',
      has_pro_pass: String(identity.hasProPass ?? false),
      has_designer_pass: String(identity.hasDesignerPass ?? false),
      app_locale: identity.appLocale,
      platform: 'web',
    });
  });
}

/**
 * Keep internal QA/team (`is_test`) traffic out of the GA property. Call
 * wherever the profile (re)loads, so a flag flipped server-side takes effect
 * on the next refresh. Set unconditionally (not only when disabling): a
 * previous test session must re-enable collection when a non-test user signs
 * in on the same browser, otherwise the off state would stick.
 */
export function setAnalyticsCollectionForUser(isTest: boolean): void {
  void setAnalyticsCollection(!isTest);
}
